import {
    MAX_IMAGE_SIZE_MB,
    MAX_UPLOAD_SIZE_MB,
} from '../config/file-constants';
import {
    generateValidationMessage,
    INVALID_FILE_EXTENSIONS_MESSAGE,
    INVALID_FILE_NAMES_MESSAGE,
    INVALID_MAX_SIZE_MESSAGE,
    INVALID_MAX_UPLOAD_MESSAGE,
    INVALID_MIN_SIZE_MESSAGE,
    INVALID_MIN_UPLOAD_MESSAGE,
    INVALID_TOTAL_UPLOAD_SIZE_MESSAGE,
} from '../config/validation-messages';
import { isImageFile } from './is-image-file';

interface FileValidationProps {
    files: File[];
    validationOptions: FileValidationOptions;
}

export interface FileValidationOptions {
    /**
     * The minimum and maximum number of files allowed.
     */
    uploadLimit?: MinMax;
    /**
     * The minimum and maximum file size in MB.
     */
    fileSizeMB?: MinMax;
    /**
     * The allowed file extensions.
     */
    fileExtensions?: string[] | readonly string[];
    /**
     * The invalid characters not allowed in the filename.
     */
    invalidChars?: string[];
    /**
     * The maximum total file size in MB.
     */
    totalSizeLimit?: number;
}

export interface MinMax {
    min?: number;
    max?: number;
}

export type FileValidationResult = (
    | { valid: true }
    | { valid: false; message: string }
) & {
    files?: File[];
};

/**
 * Validate files for file uploads.
 * @param The files to validate, and the validation options.
 * @returns The validation result.
 */
export function validateFiles({
    files,
    validationOptions,
}: FileValidationProps): FileValidationResult {
    let result: FileValidationResult = { valid: true };
    if (validationOptions.uploadLimit) {
        result = validateUploadLimit(files, validationOptions.uploadLimit);
    }
    if (result.valid && files.length) {
        files.every((file) => {
            const fileExt = getFileExtension(file.name);
            const isImage = isImageFile(file.name);
            const fileSize = validationOptions.fileSizeMB
                ? validationOptions.fileSizeMB
                : isImage
                  ? { max: MAX_IMAGE_SIZE_MB }
                  : { max: MAX_UPLOAD_SIZE_MB };

            result = validateFileSize(file, fileSize, isImage);
            if (!result.valid) {
                return false;
            }

            result = validateFileExtension(
                fileExt,
                validationOptions.fileExtensions
            );

            if (!result.valid) {
                return false;
            }

            result = validateFileName(
                file.name,
                validationOptions.invalidChars
            );
            return result.valid;
        });
    }

    return { ...result, files };
}

function addToErrorGroup(
    groups: Record<string, FileValidationResult>,
    errorMessage: string | undefined = 'Unknown error',
    file?: File
) {
    if (!groups[errorMessage]) {
        groups[errorMessage] = {
            valid: false,
            message: errorMessage,
            files: [],
        };
    }
    if (file) {
        groups[errorMessage]?.files?.push(file);
    }
}

export function validateFilesByGroup({
    files,
    validationOptions,
}: FileValidationProps): FileValidationResult[] {
    // error message => FileValidationResult
    const resultGroups: Record<string, FileValidationResult> = {};

    // check file count limit
    let result: FileValidationResult = { valid: true };
    if (validationOptions.uploadLimit) {
        result = validateUploadLimit(files, validationOptions.uploadLimit);
    }
    if (!result.valid) {
        addToErrorGroup(resultGroups, result.message);
        return Object.values(resultGroups);
    }
    // check total file size limit
    result = validateTotalSizeLimit(files, validationOptions.totalSizeLimit);
    if (!result.valid) {
        addToErrorGroup(resultGroups, result.message);
        return Object.values(resultGroups);
    }
    files.forEach((file) => {
        const fileExt = getFileExtension(file.name);
        const isImage = isImageFile(file.name);
        const fileSize = validationOptions.fileSizeMB
            ? validationOptions.fileSizeMB
            : isImage
              ? { max: MAX_IMAGE_SIZE_MB }
              : { max: MAX_UPLOAD_SIZE_MB };

        result = validateFileSize(file, fileSize, isImage);
        if (!result.valid) {
            addToErrorGroup(resultGroups, result.message, file);
            return;
        }

        result = validateFileExtension(
            fileExt,
            validationOptions.fileExtensions
        );

        if (!result.valid) {
            addToErrorGroup(resultGroups, result.message, file);
            return;
        }

        result = validateFileName(file.name, validationOptions.invalidChars);
        if (!result.valid) {
            addToErrorGroup(resultGroups, result.message, file);
        }
        return;
    });

    return Object.values(resultGroups);
}

function validateUploadLimit(
    files: File[],
    uploadLimit: MinMax
): FileValidationResult {
    if (uploadLimit.min && files.length < uploadLimit.min) {
        return {
            valid: false,
            message: generateValidationMessage(INVALID_MIN_UPLOAD_MESSAGE, [
                uploadLimit.min.toString(),
            ]),
        };
    }
    if (uploadLimit.max && files.length > uploadLimit.max) {
        return {
            valid: false,
            message: generateValidationMessage(INVALID_MAX_UPLOAD_MESSAGE, [
                uploadLimit.max.toString(),
            ]),
        };
    }
    return { valid: true };
}

function validateTotalSizeLimit(
    files: File[],
    totalSizeLimit?: number
): FileValidationResult {
    if (totalSizeLimit && totalSizeLimit > 0) {
        let totalFileSize = 0;
        files.forEach((file) => {
            totalFileSize += file.size;
        });
        if (totalFileSize > totalSizeLimit * 1024 * 1024) {
            return {
                valid: false,
                message: generateValidationMessage(
                    INVALID_TOTAL_UPLOAD_SIZE_MESSAGE,
                    [totalSizeLimit.toString()]
                ),
            };
        }
    }
    return { valid: true };
}

function validateFileSize(
    file: File,
    fileSize: MinMax,
    isImage: boolean
): FileValidationResult {
    if (fileSize.min && file.size < fileSize.min * 1024 * 1024) {
        return {
            valid: false,
            message: generateValidationMessage(INVALID_MIN_SIZE_MESSAGE, [
                isImage ? 'Image' : 'File',
                fileSize.min.toString(),
            ]),
        };
    }
    if (fileSize.max && file.size > fileSize.max * 1024 * 1024) {
        return {
            valid: false,
            message: generateValidationMessage(INVALID_MAX_SIZE_MESSAGE, [
                isImage ? 'Image' : 'File',
                fileSize.max.toString(),
            ]),
        };
    }
    return { valid: true };
}

function validateFileName(
    fileName: string,
    invalidChars?: string[]
): FileValidationResult {
    if (invalidChars?.length) {
        const invalidLetterFound = invalidChars.some((c) =>
            fileName.includes(c)
        );

        if (invalidLetterFound) {
            return {
                valid: false,
                message: generateValidationMessage(INVALID_FILE_NAMES_MESSAGE, [
                    friendlyFileNames(invalidChars),
                ]),
            };
        }
    }
    return { valid: true };
}

function validateFileExtension(
    fileExt: string,
    extensions?: string[] | readonly string[]
): FileValidationResult {
    if (extensions?.length) {
        const extFound =
            extensions.findIndex((ext) => {
                return ext.toLowerCase() === fileExt.toLowerCase();
            }) !== -1;

        if (!extFound) {
            return {
                valid: false,
                message: generateValidationMessage(
                    INVALID_FILE_EXTENSIONS_MESSAGE,
                    [friendlyFileExtensions(extensions)]
                ),
            };
        }
    }

    return { valid: true };
}

function friendlyFileExtensions(exts: string[] | readonly string[]): string {
    const niceExts = exts.map((ext) => ext.toUpperCase()).join(', ');
    const lastComma = niceExts.lastIndexOf(', ');
    return (
        niceExts.substring(0, lastComma) +
        ' or ' +
        niceExts.substring(lastComma + 1)
    );
}

function friendlyFileNames(letters: string[]): string {
    return letters.map((ext) => ext.toUpperCase()).join('');
}

function getFileExtension(fileName: string) {
    const regex = fileName.match(/[^\\]*\.(\w+)$/);
    return regex ? (regex[1]?.toLowerCase() ?? '') : '';
}
